import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  LineElement,
  Legend,
  BarElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Line } from "react-chartjs-2";
import { barChartOptions, lineChartOptions } from "../utils/constsForCharts";
import { makeDatasetForClientShareCharts, makeDatasetForSellOut } from "../utils/makeDatasetForCharts";
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Spinner } from "@chakra-ui/react";
import PriceSearchChartsLegend from "./PriceSearchChartsLegend";
import { ArrowIcon } from "~misc/icons";
import {
  clientShareActivedCards,
  clientShareProccessData,
  searchsProccessData,
  sellOutActivedCards,
  sellOutProccessData,
} from "../utils/graphHelpers";
import { getColumnIntoKey } from "../utils/initializerHelpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartDataLabels,
  Legend,
  Tooltip
);

const PriceSearchCharts = ({ filters, filteredData, trilha, clienteId, buttonClickClear }) => {
  const chartBarRef = useRef(null);
  const chartLineRef = useRef(null);
  const totalGraphItem = 6;
  const debounceTimerRef = useRef(null);

  const currentValuesRef = useRef({
    graficoClienteSharePlayers: {},
    graficoSellOutProds: {},
    lastFilteredDataIds: {},
    lastFilterOrder: "recentes",
  });
  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(true);
  const [lastFilterOrder, setLastFilterOrder] = useState("recentes");
  const [lastFilteredDataIds, setLastFilteredDataIds] = useState({});
  const [open, setOpen] = useState(false);
  const [pesquisas, setPesquisas] = useState([]);

  const [graficoSellOutProds, setGraficoSellOutProds] = useState({});
  const [graficoSellOut, setGraficoSellOut] = useState([]);
  const [graficoSellOutProdutos, setGraficoSellOutProdutos] = useState([]);
  const [graficoSellOutLabels, setGraficoSellOutLabels] = useState([]);

  const [graficoClienteSharePlayers, setGraficoClienteSharePlayers] = useState({});
  const [graficoClientShare, setGraficoClientShare] = useState([]);
  const [graficoClientShareLabels, setGraficoClientShareLabels] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    currentValuesRef.current = {
      graficoClienteSharePlayers,
      graficoSellOutProds,
      lastFilteredDataIds,
      lastFilterOrder,
    };
  }, [graficoClienteSharePlayers, graficoSellOutProds, lastFilteredDataIds, lastFilterOrder]);

  const debounce = useCallback((func, delay) => {
    return (...args) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => func(...args), delay);
    };
  }, []);

  const memoizedFilteredData = useMemo(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) return [];

    return filteredData.map((item) => ({
      id: item.id,
      ...item,
    }));
  }, [filteredData]);

  const memoizedFilters = useMemo(() => {
    return {
      order: filters?.order || "recentes",
      ...filters,
    };
  }, [filters]);

  const fetchGraficoSellOut = async () => {
    if (!Array.isArray(pesquisas) || pesquisas.length === 0) {
      setGraficoSellOutProds({});
      setGraficoSellOutProdutos([]);
      setGraficoSellOutLabels([]);
      setGraficoSellOut([]);
      return;
    }

    let graphData = pesquisas.reduce((acc, e) => acc.concat(e.values || []), []);

    if (graphData?.length) {
      const { produtos, labels, prods } = sellOutProccessData({ searchs: graphData });

      setGraficoSellOutProds(prods || {});
      setGraficoSellOutProdutos(produtos || []);
      setGraficoSellOutLabels(labels || []);
      setGraficoSellOut(Object.values(prods || {}));
    }
  };

  const fetchGraficoClientShare = async () => {
    if (!Array.isArray(pesquisas) || pesquisas.length === 0) {
      setGraficoClienteSharePlayers({});
      setGraficoClientShareLabels([]);
      setGraficoClientShare([]);
      return;
    }

    let graphData = pesquisas.reduce((acc, e) => acc.concat(e.values || []), []);

    if (graphData?.length) {
      const { labels, players } = clientShareProccessData({ searchs: graphData, totalGraphItem, trilha });

      setGraficoClienteSharePlayers(getColumnIntoKey({ data: players || [], prefix: "id-" }));
      setGraficoClientShareLabels(labels || []);
      setGraficoClientShare(Object.values(players || {}));
    }
  };

  const processChartData = useCallback(
    async (currentFilteredData, currentFilters, currentClienteId) => {
      if (!Array.isArray(currentFilteredData) || currentFilteredData.length === 0) {
        setPesquisas([]);
        return;
      }

      setLoading(true);

      try {
        let objectData = {};
        let ids = {};
        let allIdsEqual =
          Object.keys(currentValuesRef.current.lastFilteredDataIds).length === currentFilteredData.length;

        currentFilteredData.forEach((e) => {
          const key = `id-${e.id}`;
          objectData[key] = e;
          ids[key] = true;

          if (allIdsEqual && !currentValuesRef.current.lastFilteredDataIds[key]) {
            allIdsEqual = false;
          }
        });

        const compareOrderFilter = currentFilters?.order === currentValuesRef.current.lastFilterOrder;
        setLastFilterOrder(currentFilters?.order);
        setLastFilteredDataIds(ids);

        if (!allIdsEqual || !compareOrderFilter) {
          const temp = await searchsProccessData({
            func: (c) =>
              c.pesquisaProdutoId && !c.clienteFechado && !c.deletadoEm && !c.tempAberto && ids[`id-${c.id}`],
            objectData,
            totalGraphItem,
            filters: currentFilters,
            clienteId: currentClienteId,
          });

          setPesquisas(temp || []);
        } else {
          const temp = await searchsProccessData({
            func: (c) => c.pesquisaProdutoId && !c.clienteFechado && !c.deletadoEm && !c.tempAberto && c.ativo,
            objectData,
            totalGraphItem,
            filters: currentFilters,
            clienteId: currentClienteId,
          });

          if (temp?.length) {
            const playersClientShare = clientShareActivedCards({
              searchs: temp,
              totalGraphItem,
              graficoClienteSharePlayers: currentValuesRef.current.graficoClienteSharePlayers,
              trilha,
            });

            if (playersClientShare && Array.isArray(playersClientShare) && playersClientShare?.length) {
              setGraficoClienteSharePlayers(playersClientShare);
              setGraficoClientShare(playersClientShare);
            }

            const prodsSellOut = sellOutActivedCards({
              searchs: temp,
              graficoSellOutProds: currentValuesRef.current.graficoSellOutProds,
              totalGraphItem,
            });

            if (prodsSellOut && typeof prodsSellOut === "object" && Object.keys(prodsSellOut)?.length) {
              setGraficoSellOutProds(prodsSellOut);
              setGraficoSellOut(Object.values(prodsSellOut));
            }
          }
        }
      } catch (error) {
        setPesquisas([]);
      } finally {
        setLoading(false);
        setIsFirstTimeLoad(false);
      }
    },
    [trilha]
  );

  const debouncedProcessChartData = useMemo(() => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => processChartData(...args), 300);
    };
  }, [processChartData]);

  useEffect(() => {
    if (memoizedFilteredData?.length > 0) {
      debouncedProcessChartData(memoizedFilteredData, memoizedFilters, clienteId);
    }
  }, [memoizedFilteredData, memoizedFilters, clienteId, debouncedProcessChartData]);

  useEffect(() => {
    if (Array.isArray(pesquisas) && pesquisas.length > 0) {
      (async () => {
        await Promise.all([fetchGraficoClientShare(), fetchGraficoSellOut()]);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pesquisas]);

  const formatgraficoClientShare = useMemo(() => {
    if (!graficoClientShare || !Array.isArray(graficoClientShare) || graficoClientShare.length === 0) {
      return [];
    }

    return graficoClientShare.map((empresa) => ({
      ...empresa,
      data: empresa.data ? empresa.data.map((data) => (data !== "" ? data : "")) : [],
    }));
  }, [graficoClientShare]);

  const chartData = useMemo(() => {
    const validFormatgraficoClientShare = Array.isArray(formatgraficoClientShare) ? formatgraficoClientShare : [];
    const validGraficoSellOut = Array.isArray(graficoSellOut) ? graficoSellOut : [];
    const validGraficoClientShareLabels = Array.isArray(graficoClientShareLabels) ? graficoClientShareLabels : [];
    const validGraficoSellOutLabels = Array.isArray(graficoSellOutLabels) ? graficoSellOutLabels : [];

    return {
      clientShare: {
        labels: validGraficoClientShareLabels,
        datasets: makeDatasetForClientShareCharts(validFormatgraficoClientShare),
      },
      sellOut: {
        labels: validGraficoSellOutLabels,
        datasets: makeDatasetForSellOut(validGraficoSellOut),
      },
    };
  }, [formatgraficoClientShare, graficoSellOut, graficoClientShareLabels, graficoSellOutLabels]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [accordionIndex, setAccordionIndex] = useState([]);

  const debouncedHandleResize = useMemo(() => debounce(() => setWindowWidth(window.innerWidth), 200), [debounce]);

  useEffect(() => {
    window.addEventListener("resize", debouncedHandleResize);
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useEffect(() => {
    const isMobileView = windowWidth < 1201;

    if (isMobileView) {
      setOpen(graficoClientShare.length > 0);
      setAccordionIndex(graficoClientShare.length > 0 ? [0] : []);
    } else {
      setOpen(true);
      setAccordionIndex([0]);
    }
  }, [windowWidth, graficoClientShare]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset charts when buttonClickClear changes
  useEffect(() => {
    if (buttonClickClear) {
      // Reset all chart states
      setGraficoSellOutProds({});
      setGraficoSellOut([]);
      setGraficoSellOutProdutos([]);
      setGraficoSellOutLabels([]);
      setGraficoClienteSharePlayers({});
      setGraficoClientShare([]);
      setGraficoClientShareLabels([]);
      setPesquisas([]);
      setLastFilteredDataIds({});
      setLastFilterOrder("recentes");
      setIsFirstTimeLoad(true);

      // Reset current values ref
      currentValuesRef.current = {
        graficoClienteSharePlayers: {},
        graficoSellOutProds: {},
        lastFilteredDataIds: {},
        lastFilterOrder: "recentes",
      };
    }
  }, [buttonClickClear]);

  return (
    <div className="price-search-charts" data-testid="price-search-charts">
      {loading && isFirstTimeLoad ? (
        <div className="flex items-center justify-center w-full p-8" data-testid="charts-loading">
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.main" size="xl" />
        </div>
      ) : (
        <div className="charts-container" data-testid="charts-container">
          {trilha === "TAC" && (
            <Accordion
              index={accordionIndex}
              allowToggle={true}
              className={`${open ? "open" : ""}`}
              onChange={(index) => setAccordionIndex(index)}
              data-testid="tac-accordion"
            >
              <AccordionItem data-testid="tac-accordion-item">
                <AccordionButton onClick={() => setOpen(!open)} data-testid="tac-accordion-button">
                  <div className="title-container" data-testid="tac-title-container">
                    <h2 className="title">Client Share (Em toneladas)</h2>
                    <h2 className="title">Sell Out (Reais por unidade)</h2>
                  </div>
                  <ArrowIcon className="icon" />
                </AccordionButton>
                <AccordionPanel data-testid="tac-accordion-panel">
                  <div className="charts-row" data-testid="tac-charts-row">
                    <div className="chart-container" data-testid="client-share-container">
                      <h2 className="chart-title-responsive">Client Share</h2>
                      <Bar
                        ref={chartBarRef}
                        options={barChartOptions}
                        data={chartData.clientShare}
                        data-testid="client-share-chart"
                      />
                      <PriceSearchChartsLegend
                        items={{
                          organizedItems: graficoClientShare,
                        }}
                        title="Volume potencial"
                        tipo="barra"
                      />
                    </div>

                    <div className="chart-container" data-testid="sell-out-container">
                      <h2 className="chart-title-responsive">Sell Out</h2>
                      <Line
                        ref={chartLineRef}
                        options={lineChartOptions}
                        data={chartData.sellOut}
                        data-testid="sell-out-chart"
                      />
                      <PriceSearchChartsLegend
                        items={{
                          organizedItems: graficoSellOut,
                        }}
                        itemsOthers={graficoSellOutProdutos}
                        title="Quantidade de Players:"
                        tipo="linha"
                      />
                    </div>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}

          {trilha === "IND" && (
            <Accordion
              index={accordionIndex}
              allowToggle={true}
              className={`${open ? "open" : ""}`}
              onChange={(index) => setAccordionIndex(index)}
              data-testid="ind-accordion"
            >
              <AccordionItem data-testid="ind-accordion-item">
                <AccordionButton onClick={() => setOpen(!open)} data-testid="ind-accordion-button">
                  <div className="title-container" data-testid="ind-title-container">
                    <h2 className="title">Client Share</h2>
                  </div>
                  <ArrowIcon className="icon" />
                </AccordionButton>
                <AccordionPanel data-testid="ind-accordion-panel">
                  <div
                    className="shrink grow basis-1/2 md:p-8 border-r mb-8 md:mb-0 max-w-[560px] mx-auto"
                    data-testid="ind-chart-container"
                  >
                    <Bar
                      ref={chartBarRef}
                      options={barChartOptions}
                      data={chartData.clientShare}
                      data-testid="ind-client-share-chart"
                    />
                    <PriceSearchChartsLegend
                      items={{
                        organizedItems: graficoClientShare,
                      }}
                      title="Volume potencial"
                      tipo="barra"
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
};

PriceSearchCharts.propTypes = {
  filters: PropTypes.shape({
    produto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    grupoProduto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    periodo: PropTypes.oneOf(["hoje", "seteDias", "quinzeDias", "trintaDias"]),
    order: PropTypes.oneOf(["recentes", "asc"]),
  }),
  filteredData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      dadosGerais: PropTypes.object,
      values: PropTypes.array,
    })
  ),
  trilha: PropTypes.oneOf(["TAC", "IND"]).isRequired,
  clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  buttonClickClear: PropTypes.func,
};

PriceSearchCharts.defaultProps = {
  filters: {},
  filteredData: [],
  buttonClickClear: null,
};

export default PriceSearchCharts;
